/**
 * Card #2937 — who or what acted on a file, in the words a staffer reads. The ONE mapping every
 * actor stamp on the file page goes through (the Risk block's "Assessed by" / "Approved by", an
 * open item's "Waived by").
 *
 * <p>An actor stamp is the audit trail's `actor` value, passed through verbatim by the engine. A
 * HUMAN stamp carries a rail prefix in front of the person's identity
 * (`human-console:dana@example-firm.co.uk`, or `operator:dana@example-firm.co.uk` from a staff form or
 * an operator JWT — `StaffFormsApi.cs`, `InternalApiBearerFilter.cs`); the prefix is taken off and
 * the person is named. A
 * MACHINE stamp (`platform:scheduler`, `agent-runner:claude-sdk-v0`, `subject:{hash}:{hash}`, …) is
 * an internal value and is never shown (L-97, form rule 7): it reads as plain words naming what
 * acted. A machine family this portal has no words for yet still reads as a plain phrase, never
 * the raw code. What the audit trail stores is unchanged — this is display only.</p>
 *
 * <p>The families are the ones the engine stamps (`platform/core/Auth/ActorPrefixes.cs`,
 * `TaskMeterOptions`'s counted/excluded lists, and `packages/design-system/src/actorClass.ts`, whose
 * fence walks every actor literal under `platform/**`). The scheduler's words are
 * `./arrivedVia.ts`'s, so "Arrived via" and the actor say the same thing.</p>
 */
import { PLATFORM_START_WORDS } from "./arrivedVia.js";

/** The rails that stamp a PERSON: the prefix comes off and what follows is their identity. */
export const HUMAN_ACTOR_PREFIXES: readonly string[] = [
  "human-console:",
  "human-cli:",
  "human-chat:",
  "operator:",
];

/** An AI agent acting on the file (the engine's agent runner, or an agent at the CLI). */
export const AI_AGENT = "AI agent";
/** A connected system's webhook, routed in by the platform. */
export const CONNECTED_SYSTEM = "Automatic update from a connected system";
/** The client, acting through a form the platform sent them. */
export const CLIENT_FORM = "The client, through their form";
/** A member of the public on the website chat. */
export const WEBSITE_VISITOR = "A website visitor";
/** A person on the file, acting through their own link. */
export const PERSON_ON_FILE = "A person on the file";
/** One of our own test runs. */
export const TEST_RUN = "A test run";
/** Any other platform step, or a machine family this portal has no words for yet. */
export const PLATFORM_STEP = "The platform";

/**
 * Each machine family, by prefix, in plain words. Checked in order; the first match wins, so a
 * whole value (`platform:webhook-router`) sits above its family (`platform:`).
 */
export const MACHINE_ACTOR_WORDS: ReadonlyArray<readonly [prefix: string, words: string]> = [
  ["agent-runner:", AI_AGENT],
  ["agent-cli:", AI_AGENT],
  ["stub-agent-runner", AI_AGENT],
  ["platform:webhook-router", CONNECTED_SYSTEM],
  ["webhook:", CONNECTED_SYSTEM],
  ["platform:", PLATFORM_STEP],
  ["subject:", CLIENT_FORM],
  ["form:", CLIENT_FORM],
  ["guest:", WEBSITE_VISITOR],
  ["guest-session:", WEBSITE_VISITOR],
  ["guest-link:", WEBSITE_VISITOR],
  ["party:", PERSON_ON_FILE],
  ["chat:teams:", "Someone in Teams"],
  ["chat:slack:", "Someone in Slack"],
  ["chat:google_chat:", "Someone in Google Chat"],
  ["qa:", TEST_RUN],
];

/** A machine-shaped stamp: a lower-case family code and a colon in front. */
const MACHINE_SHAPE = /^[a-z][a-z0-9_-]*:/;

/**
 * An actor stamp as a person reads it: a human's identity with the rail prefix taken off, or a
 * machine named in plain words. A stamp with no prefix at all (a bare name) is printed as recorded.
 */
export function actorName(actor: string): string {
  const value = actor.trim();
  for (const prefix of HUMAN_ACTOR_PREFIXES) {
    if (value.startsWith(prefix)) return value.slice(prefix.length);
  }
  const scheduled = PLATFORM_START_WORDS.get(value);
  if (scheduled !== undefined) return scheduled;
  for (const [prefix, words] of MACHINE_ACTOR_WORDS) {
    if (value.startsWith(prefix)) return words;
  }
  if (MACHINE_SHAPE.test(value)) return PLATFORM_STEP;
  return actor;
}
